import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #headerStickyState;
  #transitions = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    const header = this.closest('#header-component');
    this.#restoreHeaderState(header);
    header?.removeAttribute('data-mobile-drawer-open');
    header?.removeAttribute('data-mobile-drawer-restoring');
    header?.style.removeProperty('--mobile-drawer-header-top');
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  toggle(event) {
    event?.preventDefault();

    if (this.refs.details.classList.contains('menu-open')) return this.close();

    this.refs.details.setAttribute('open', '');
    return this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transition = {};
    this.#transitions.set(details, transition);
    const header = this.closest('#header-component');
    if (!header?.hasAttribute('data-mobile-drawer-open')) {
      this.#headerStickyState = header?.getAttribute('data-sticky-state') ?? null;
      const headerTop = header?.getBoundingClientRect().top ?? 0;
      header?.style.setProperty('--mobile-drawer-header-top', `${headerTop}px`);
    }
    header?.setAttribute('data-sticky-state', 'active');
    header?.setAttribute('data-mobile-drawer-open', '');
    header?.removeAttribute('data-mobile-drawer-restoring');

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    details.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (this.#transitions.get(details) !== transition) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (this.#transitions.get(details) === transition) trapFocus(details);
      }, { subtree: false });
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transition = {};
    this.#transitions.set(details, transition);
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (this.#transitions.get(details) !== transition) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          const header = this.closest('#header-component');
          header?.setAttribute('data-mobile-drawer-restoring', '');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.#transitions.get(details) !== transition || !this.isConnected) return;
              this.#restoreHeaderState(header);
              header?.removeAttribute('data-mobile-drawer-open');
              header?.style.removeProperty('--mobile-drawer-header-top');
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (this.#transitions.get(details) === transition) {
                    header?.removeAttribute('data-mobile-drawer-restoring');
                  }
                });
              });
            });
          });
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  #restoreHeaderState(header) {
    if (!header) return;

    if (this.#headerStickyState === null) {
      header.removeAttribute('data-sticky-state');
    } else if (this.#headerStickyState !== undefined) {
      header.setAttribute('data-sticky-state', this.#headerStickyState);
    }

    this.#headerStickyState = undefined;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
